import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { MaskedLines } from "@/components/Reveal";
import { whatsappLink, IMAGES } from "@/config";
import { easeLux } from "@/lib/anim";

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
    const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
    const textY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

    return (
        <section
            id="top"
            ref={ref}
            className="relative min-h-[100svh] overflow-hidden pt-24 sm:pt-28"
            data-testid="hero-section"
        >
            <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-5 pb-16 sm:px-8 lg:grid-cols-12 lg:gap-6 lg:pb-24">
                {/* Left: copy */}
                <motion.div style={{ y: textY }} className="lg:col-span-7 lg:pr-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: easeLux }}
                        className="mb-6 flex items-center gap-3"
                    >
                        <span className="h-px w-10 bg-[var(--fama-gold)]" />
                        <span className="font-body text-xs font-semibold uppercase tracking-[0.28em] text-[var(--fama-gold)]">
                            Property Management · Roma
                        </span>
                    </motion.div>

                    <MaskedLines
                        as="h1"
                        animateOnLoad
                        delayChildren={0.25}
                        stagger={0.12}
                        lines={["Il tuo immobile,", "al massimo", "potenziale."]}
                        className="font-serif-display text-[15vw] font-medium leading-[0.94] tracking-[-0.02em] text-[var(--fama-ink)] sm:text-7xl lg:text-[4.8rem] xl:text-[5.4rem]"
                        lineClassName=""
                    />

                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: easeLux, delay: 0.9 }}
                        className="mt-7 max-w-xl font-body text-base leading-relaxed text-[var(--fama-ink-soft)] sm:text-lg"
                    >
                        Gestiamo la tua struttura ricettiva a Roma come fosse
                        nostra: burocrazia, allestimento, annunci, ospiti e
                        pulizie certificate. Tu ricevi i rendimenti, senza
                        pensieri.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: easeLux, delay: 1.05 }}
                        className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center"
                    >
                        <a
                            href={whatsappLink()}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-whatsapp-button"
                            className="group inline-flex items-center justify-center gap-3 rounded-full bg-[var(--wa-green)] px-8 py-4 font-body text-base font-semibold text-white shadow-lg shadow-[rgba(33,178,82,0.25)] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[var(--wa-green-hover)]"
                        >
                            <WhatsAppIcon size={20} />
                            Scrivici su WhatsApp
                        </a>
                        <a
                            href="#servizi"
                            onClick={(e) => {
                                e.preventDefault();
                                document
                                    .querySelector("#servizi")
                                    ?.scrollIntoView({ behavior: "smooth" });
                            }}
                            data-testid="hero-services-link"
                            className="group inline-flex items-center justify-center gap-2 font-body text-base font-medium text-[var(--fama-ink)]"
                        >
                            Scopri i servizi
                            <ArrowDown
                                size={18}
                                className="transition-transform duration-300 group-hover:translate-y-1"
                            />
                        </a>
                    </motion.div>
                </motion.div>

                {/* Right: image with parallax + gold hairline frame */}
                <div className="lg:col-span-5">
                    <motion.div
                        initial={{ opacity: 0, scale: 1.04 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.2, ease: easeLux, delay: 0.35 }}
                        className="relative aspect-[4/5] overflow-hidden rounded-[2px] ring-1 ring-[var(--fama-border)] lg:aspect-auto lg:h-[66vh]"
                    >
                        <motion.img
                            src={IMAGES.hero}
                            alt="Interno di un appartamento boutique curato e luminoso a Roma"
                            style={{ y: imgY, scale: imgScale }}
                            className="absolute inset-0 h-full w-full object-cover"
                            loading="eager"
                        />
                        <div className="pointer-events-none absolute inset-3 border border-white/40" />
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: easeLux, delay: 1.1 }}
                        className="mt-5 flex items-center gap-4"
                    >
                        <p className="font-serif-display text-5xl leading-none text-[var(--fama-gold)]">
                            360°
                        </p>
                        <p className="font-body text-sm leading-snug text-[var(--fama-ink-soft)]">
                            Servizio completo
                            <br />
                            chiavi in mano
                        </p>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
